import { useEffect, useState } from 'react';
import { useOrderApiV1 } from '../../../../../clients/quipli-service/order/order-api-v1';
import { toast } from '../../../../../util/toasts';
import { useAPI } from '../../../../../clients/quipli-service/quipli-service-client';
import {
  InventoryReturnRequest,
  Order,
  OrderReturnStatus,
  OrderType,
} from '../../../../../clients/quipli-service/order/order';
import { Configuration } from '../../../../../clients/quipli-service/configuration/configuration';
import { Location } from '../../../../../clients/quipli-service/location/location';
import { Dictionary, keyBy } from 'lodash';

export const useOffRentConfig = (orderId: number) => {
  const { getMultipleConfigurations, listLocations } = useAPI();
  const { getOrderById } = useOrderApiV1();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [unitTrackingType, setUnitTrackingType] = useState<'serialNumber' | 'unitNumber'>('unitNumber');
  const [order, setOrder] = useState<Order>();
  const [configs, setConfigs] = useState<Dictionary<Configuration>>();
  const [onOffRentUpdateDateConfiguration, setOnOffRentUpdateDateConfiguration] = useState<boolean>(false);
  const [locations, setLocations] = useState<Location[]>();
  const [showSelectProductsStep, setShowSelectProductsStep] = useState<boolean>(true);
  const [inventoryReturnRequest, setInventoryReturnRequest] = useState<InventoryReturnRequest>();

  // TODO: needs loading indicators
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      getOrderById(orderId)
        .then(setOrder)
        .catch((e) => toast(e.message, 'error'));
      getMultipleConfigurations([
        'CONF_DEFAULT_RENTAL_END_TIME',
        'CONF_UNIT_TRACKING_VIA',
        'CONF_ALLOW_COMBINED_OFF_RENT_RETURN',
        'CONF_GRACE_PERIOD_FOR_RETURNS',
        'CONF_SEIS_INTEGRATION',
        'CONF_FUEL_UNIT',
        'CONF_ON_OFF_RENT_UPDATE_ORDER_DATE',
      ])
        .then((confs) => {
          setUnitTrackingType(
            confs.find((c) => c.variable === 'CONF_UNIT_TRACKING_VIA')?.value === '1' ? 'serialNumber' : 'unitNumber',
          );
          setConfigs(keyBy(confs, 'variable'));
          setOnOffRentUpdateDateConfiguration(
            confs.find((c) => c.variable === 'CONF_ON_OFF_RENT_UPDATE_ORDER_DATE')?.value?.includes('offrent') ?? false,
          );
        })
        .catch((e) => {
          console.log(e);
          toast('Error loading configurations', 'error');
        });
      listLocations()
        .then(setLocations)
        .catch((e) => {
          console.log(e);
          toast('Error loading locations', 'error');
        });
    };
    init().finally(() => setIsLoading(false));
  }, []);

  // TODO: this should be part of the init call, doesn't need its own use effect
  useEffect(() => {
    if (order && configs && configs['CONF_ALLOW_COMBINED_OFF_RENT_RETURN']?.value === '1') {
      const orderProducts = order.products.filter(
        (op) => op.type === OrderType.Rental && op.status === OrderReturnStatus.OnRent,
      );
      if (showSelectProductsStep) {
        setInventoryReturnRequest({
          orderProductIds: orderProducts.filter((op) => op.status === OrderReturnStatus.OnRent).map((op) => op.id),
          returnToLocation: order.locationId,
          hourReadings: orderProducts
            .filter((op) => op.product.allowHourTracking)
            .flatMap((op) => op.assignedUnits)
            .map((au) => {
              return { unitId: au.id, hourReading: au.lastHourReading?.hour || 0 };
            }),
          chargeSecurityDeposit: false,
        });
      }
    }
  }, [configs, order]);

  const noProductsToOffRent =
    order && !order?.products.some((op) => op.type === OrderType.Rental && op.status === OrderReturnStatus.OnRent);
  const showInventoryReturn = configs && configs['CONF_ALLOW_COMBINED_OFF_RENT_RETURN']?.value === '1';

  return {
    unitTrackingType,
    order,
    configs,
    onOffRentUpdateDateConfiguration,
    locations,
    showSelectProductsStep,
    inventoryReturnRequest,
    isLoading,
    setInventoryReturnRequest,
    setShowSelectProductsStep,
    setOnOffRentUpdateDateConfiguration,
    setLocations,
    setUnitTrackingType,
    setConfigs,
    setOrder,
    setIsLoading,
    noProductsToOffRent,
    showInventoryReturn,
  };
};
